/**
 * Page Configuration Module
 *
 * Reads static page config from server-rendered JSON.
 * All dynamic data (user, tracking, csrf) comes from globals.json via primeval.js.
 *
 * This replaces the old _setWindowGlobals.erb pattern with a cleaner approach:
 * - Static config in JSON (no inline JS execution)
 * - Clear separation between static config and dynamic data
 * - Type-safe access via getPageConfig()
 *
 * Note: Lazy loading is now handled by Stimulus controllers via data-controller attributes.
 * See: app/javascript/controllers/ for feature-specific controllers.
 *
 * @example
 * import { getPageConfig, env, locale } from 'common/page_config'
 *
 * // Get full config
 * const config = getPageConfig()
 *
 * // Or use individual exports
 * if (env === 'development') { ... }
 */

let _config = null

/**
 * Parse and cache the page config from the JSON script tag
 * @returns {Object} Page configuration
 */
export function getPageConfig() {
  if (_config) return _config

  const configEl = document.getElementById('page-config')
  if (!configEl) {
    console.warn('[PageConfig] #page-config element not found, using defaults')
    _config = {
      env: 'production',
      locale: 'en-US',
      localeCountry: 'us',
      subdomain: null,
      isWww: false,
      isCrm: false,
      liveChatEnabled: false
    }
    return _config
  }

  try {
    _config = JSON.parse(configEl.textContent)
  } catch (e) {
    console.error('[PageConfig] Failed to parse page config:', e)
    _config = {
      env: 'production',
      locale: 'en-US',
      localeCountry: 'us',
      subdomain: null,
      isWww: false,
      isCrm: false,
      liveChatEnabled: false
    }
  }

  return _config
}

// Convenience getters (lazy-evaluated)
export const env = () => getPageConfig().env
export const locale = () => getPageConfig().locale
export const localeCountry = () => getPageConfig().localeCountry
export const subdomain = () => getPageConfig().subdomain
export const isWww = () => getPageConfig().isWww
export const isCrm = () => getPageConfig().isCrm
export const liveChatEnabled = () => getPageConfig().liveChatEnabled

/**
 * Initialize window globals for backward compatibility
 * This bridges old code that reads window.env, window.locale, etc.
 * Call this early in the bundle entry point.
 *
 * @deprecated Prefer importing from page_config directly
 */
export function initWindowGlobals() {
  const config = getPageConfig()

  // Set window globals for backward compatibility
  window.env = config.env
  window.subdomain = config.subdomain
  window.locale = config.locale
  window.localeCountry = config.localeCountry
  window.LC_ENABLED = config.liveChatEnabled

  // Initialize empty arrays/objects that other code expects
  window._wq = window._wq || [] // Wistia queue
  window.$crisp = window.$crisp || [] // Crisp chat

  // These will be set by globals.json (primeval.js)
  window.pageCsrf = window.pageCsrf || { param: 'authenticity_token', token: null }
  window.webpack_nonce = window.webpack_nonce || null
  window.RMTREV = window.RMTREV || null
  window.current_user = window.current_user || null
  window.tracVis = window.tracVis || null
  window.gdpr_country = window.gdpr_country || null
  window.error_queue = window.error_queue || null
  window.warning_queue = window.warning_queue || null
  window.notice_queue = window.notice_queue || null
  window.live_event_banner = window.live_event_banner || null
}

// Auto-initialize window globals when this module is imported.
// This fixes a race condition where ES6 import hoisting causes other modules
// to check window.env before the explicit initWindowGlobals() call runs.
// Safe to call multiple times - getPageConfig() returns cached result.
initWindowGlobals()


